import "./SurveyEditSeriesPage.scss";

import { Alert, ExclamationTriangleIcon, Flex, Text } from "@fluentui/react-northstar";
import { DialogDimension, dialog } from "@microsoft/teams-js";
import { QuestionnaireTemplateDTO, ScheduleDTO, SurveyDTO } from "@teams/types/api";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation, useParams } from "react-router";

import { useSeries } from "../../_shared/actions/useSeries";
import { AlertError } from "../../_shared/components/AlertError";
import { EditScheduleDialog } from "../../_shared/components/Menus/SeriesMenu/EditScheduleDialog";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { useTeamsContext } from "../../_shared/state/TeamsContextProvider";
import { AgilePollyAuthoringForm } from "../../agile/pages/components/AgilePollyAuthoringForm";
import {
  AuthoringFields,
  PollySurveyAuthoringForm,
} from "./SurveyAuthoring/PollySurveyAuthoringForm";

export const SurveyEditSeriesPage: React.FC<RouteComponentProps> = props => {
  const history = useHistory();
  const { seriesId } = useParams<{ seriesId: string }>();
  const { teamsContext } = useTeamsContext();
  const { error, loadSeries, isSeriesLoading, series } = useSeries(true);
  const [surveyData, setSurveyData] = useState<AuthoringFields>();
  const [showEditSelector, setShowEditSelector] = useState<boolean>();
  const [limitedEdit, setLimitedEdit] = useState<boolean>();

  const { search } = useLocation();

  useEffect(() => {
    const urlParms = new URLSearchParams(search);
    setLimitedEdit(
      urlParms.has("limitedEdit") ? urlParms.get("limitedEdit") === "true" : undefined
    );
  }, [search]);

  useEffect(() => {
    if (seriesId) {
      loadSeries(teamsContext, seriesId);
    }
  }, [seriesId, teamsContext, loadSeries]);

  useEffect(() => {
    if (!series) {
      return;
    }
    setShowEditSelector(series.lastSurvey?.state === "open");
    const parsed = surveyDataFromSeries(series, limitedEdit ? series.lastSurvey : undefined);
    setSurveyData(parsed);
  }, [series, props.location.search, teamsContext, limitedEdit]);

  useEffect(() => {
    if (showEditSelector === false) {
      dialog.update.resize({ height: DialogDimension.Large, width: 700 });
    }
  }, [showEditSelector]);

  if (error) {
    return <AlertError error={error} onClick={() => loadSeries(teamsContext, seriesId)} />;
  }

  if (!surveyData || isSeriesLoading || !series) {
    return <TeamsLoader isLoading loadingMessage="Loading..." />;
  }

  if (showEditSelector && limitedEdit === undefined) {
    return (
      <Flex fill hAlign="center">
        <Flex column className="edit-selector-container">
          <EditScheduleDialog
            setIsEditSelectionOpen={setShowEditSelector}
            setIsLimitedEdit={setLimitedEdit}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex column gap="gap.small" className="polly-edit-recurring-page">
      {!limitedEdit && (
        <Alert
          warning
          icon={<ExclamationTriangleIcon size="large" />}
          content={<Text weight="bold" content="Changes will only apply to future instances!" />}
        />
      )}

      {(surveyData.type === "poll" ||
        surveyData.type === "survey" ||
        surveyData.type === "quiz") && (
        <PollySurveyAuthoringForm
          editScheduleId={series.id}
          defaultValues={surveyData}
          isEdit={true}
          isLimitedEditForRecurring={limitedEdit}
          allowScheduledSend={true}
          showLoading={isSeriesLoading}
          {...(limitedEdit && { parentSurvey: series.lastSurvey })}
          onSaved={() => {
            if (teamsContext.page.frameContext === "task") {
              dialog.update.resize({ height: DialogDimension.Small, width: 700 });
              history.replace(
                `/msteams/content/polly/${encodeURIComponent(
                  series.id
                )}/success?update=true&recurring=true`
              );
            }
          }}
        />
      )}

      {surveyData.type === "agile-standup" && (
        <AgilePollyAuthoringForm
          editScheduleId={series.id}
          surveyData={surveyData}
          reminderSizeLimit={25}
        />
      )}
    </Flex>
  );
};

function surveyDataFromSeries(series: ScheduleDTO, lastSurvey?: SurveyDTO): AuthoringFields {
  const dto: SurveyDTO | QuestionnaireTemplateDTO = lastSurvey ?? series.template;

  return {
    title: dto.title,
    type: dto.type,
    description: dto.description,
    anonymity: dto.anonymity,
    sendAt: moment(series.rruleDTStart).toDate(),
    questions: dto.questions,
    commentsEnabled: dto.commentsEnabled,
    commentsAppeal: dto.commentsAppeal || "",
    resultsVisibility: dto.resultsVisibility,
    rrule: series.rrule as string,
    // For recurring pollys, openDuration is not set for the survey instances.
    // Close date is calculated using the openDuration of the template when sending the survey instance
    openDuration: series.template.openDuration,
    reminders: dto.reminders,
    deliveryMethod: dto.deliveryPreference || "in-group",
    notifyUsersOnEdit: lastSurvey ? lastSurvey.responseCount > 0 : false,
    removeExistingVotesOnEdit: false,
  };
}
